import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import StatsCard from '../../components/common/StatsCard';
import {
  Download,
  Filter,
  CreditCard,
  ArrowDownRight,
  ArrowUpRight,
  RefreshCcw,
} from 'lucide-react';
import { PAYMENT_FILTER_FIELDS } from '../../constants/filters';
import { getStatusColor } from '../../utils';
import PageHeader from '../../components/common/PageHeader';

type Payment = {
  id?: string;
  login?: number;
  type: 'deposit' | 'withdraw' | 'transfer';
  amount: number;
  currency?: string;
  comment?: string;
  timestamp?: string;
  status?: 'success' | 'failed';
  client?: string;
  method?: string;
};

const Payments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const filterFields = PAYMENT_FILTER_FIELDS;

  const fetchPayments = async () => {
    setIsLoading(true);
    // api integrate here
  };

  const columns: DataTableColumn<Payment>[] = [
    {
      id: 'id',
      header: 'Payment ID',
      accessor: (row: Payment) => row.id || `PMT-${row.login}`,
      sortable: true,
      sortValue: (row: Payment) => row.id || `PMT-${row.login || ''}`,
      width: '130px',
    },
    {
      id: 'client',
      header: 'Client',
      accessor: (row: Payment) => row.client || `Account ${row.login}`,
      sortable: true,
      sortValue: (row: Payment) => row.client || `Account ${row.login || ''}`,
      width: '160px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: (row: Payment) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'deposit'
              ? 'bg-green-100 text-green-800'
              : row.type === 'withdraw'
                ? 'bg-red-100 text-red-800'
                : 'bg-blue-100 text-blue-800'
          }`}
        >
          {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: (row: Payment) => row.type,
      width: '120px',
    },
    {
      id: 'method',
      header: 'Method',
      accessor: (row: Payment) => (
        <Badge
          className={getStatusColor(
            row.method?.toLowerCase().replace(' ', '-') || 'mt5',
            'payment'
          )}
        >
          {row.method || 'MT5'}
        </Badge>
      ),
      sortable: true,
      sortValue: (row: Payment) => row.method || 'MT5',
      width: '140px',
    },
    {
      id: 'currency',
      header: 'Currency',
      accessor: (row: Payment) => row.currency || 'USD',
      sortable: true,
      sortValue: (row: Payment) => row.currency || 'USD',
      width: '100px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: (row: Payment) => (
        <span
          className={
            row.type === 'withdraw'
              ? 'text-red-600 font-semibold'
              : 'text-green-600 font-semibold'
          }
        >
          {row.type === 'withdraw' ? '-' : '+'}
          {row.currency || '$'}
          {Math.abs(row.amount).toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: (row: Payment) => row.amount,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: Payment) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status === 'success' ? 'Completed' : 'Failed'}
        </span>
      ),
      sortable: true,
      sortValue: (row: Payment) => row.status || 'success',
      width: '120px',
    },
    {
      id: 'timestamp',
      header: 'Created at',
      accessor: (row: Payment) => {
        if (!row.timestamp) return '-';
        const date = new Date(row.timestamp);
        return date.toLocaleString();
      },
      sortable: true,
      sortValue: (row: Payment) => row.timestamp || '',
      width: '160px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: (row: Payment) => (
        <ActionDropdown
          id={row.id || `PMT-${row.login}`}
          onView={() => console.log('View payment:', row.id)}
          onEdit={() => console.log('Edit payment:', row.id)}
          onDelete={() => console.log('Delete payment:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Payment"
          description="Monitor and manage all client deposits, withdrawals and internal transfers."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <StatsCard
          title="Total Deposits"
          value="3"
          change="All successful deposits"
          icon={ArrowDownRight}
        />

        <StatsCard
          title="Total Withdrawals"
          value="2"
          change="All successful withdrawals"
          icon={ArrowUpRight}
        />

        <StatsCard
          title="Failed Requests"
          value="2"
          change="Failed transactions"
          icon={RefreshCcw}
        />

        <StatsCard
          title="Total Transactions"
          value="100"
          change="All deposit/withdraw operations"
          icon={CreditCard}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Payment activity</CardTitle>
              <CardDescription>
                Recent deposits, withdrawals and internal transfers across all
                clients.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPayments}
                disabled={isLoading}
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Payments"
          />
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCcw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={[]}
              searchValue={searchTerm}
              searchPlaceholder="Search by payment ID, client, method, currency, amount, ..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showAddButton={false}
              showExportButton={false}
              showColumnVisibility={true}
              initialPageSize={10}
              initialSortBy={{ id: 'timestamp', desc: true }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
